import React from 'react';
import { Divider, IconButton, Tooltip } from '@mui/material';
import { TableHeaderProps } from '../../types';
import { SearchInput } from '../toolbar/search-input';
import { FilterButton } from '../toolbar/filter-button';
import { ColumnVisibilityMenu } from '../toolbar/column-visibility-menu';
import { ViewToggleButton } from '../toolbar/view-toggle-button';
import { FullscreenButton } from '../toolbar/fullscreen-button';
import { IconPreloader } from '../states/icon-preloader';
import {
  TableHeaderContainer,
  TableHeaderActions,
  TableHeaderTitle,
} from '../styled/table-header-styles';

export const TableHeader = ({
  onSearch,
  searchValue = '',
  showHeader = true,
  isFilterActive = false,
  isFullscreen = false,
  isListView = false,
  onFilterToggle,
  onColumnsToggle,
  onFullscreenToggle,
  onList,
  columns = [],
  onColumnVisibilityChange,
  title,
  showTitle = true,
  buttonConfig = {
    showSearch: true,
    showFilter: true,
    showColumnVisibility: true,
    showViewToggle: true,
    showFullscreen: true,
  },
  customButtons = [],
}: TableHeaderProps) => {
  if (!showHeader) return null;

  // Determine if we should show the title
  const shouldShowTitle = showTitle && title;

  // Helper function to render custom buttons
  const renderCustomButtons = () => {
    if (!customButtons.length) return null;

    return (
      <>
        {customButtons.map((button) => {
          const buttonElement = (
            <IconButton
              key={button.id}
              onClick={button.onClick}
              disabled={button.disabled}
              size="small"
            >
              {button.content}
            </IconButton>
          );

          return button.tooltip ? (
            <Tooltip key={button.id} title={button.tooltip}>
              {buttonElement}
            </Tooltip>
          ) : (
            buttonElement
          );
        })}
        <Divider orientation="vertical" flexItem />
      </>
    );
  };

  // Helper function to render standard buttons with configuration
  const renderStandardButtons = () => {
    const buttons = [];

    if (buttonConfig.showSearch) {
      buttons.push(
        <SearchInput
          key="search"
          onSearch={onSearch}
          searchValue={searchValue}
        />,
      );
    }

    if (buttonConfig.showFilter) {
      buttons.push(
        <FilterButton
          key="filter"
          isFilterActive={isFilterActive}
          onFilterToggle={onFilterToggle}
        />,
      );
    }

    if (buttonConfig.showColumnVisibility) {
      buttons.push(
        <ColumnVisibilityMenu
          key="columns"
          columns={columns}
          onColumnVisibilityChange={onColumnVisibilityChange}
          onColumnsToggle={onColumnsToggle}
        />,
      );
    }

    if (buttonConfig.showViewToggle) {
      buttons.push(
        <ViewToggleButton key="view" isListView={isListView} onList={onList} />,
      );
    }

    if (buttonConfig.showFullscreen) {
      buttons.push(
        <FullscreenButton
          key="fullscreen"
          isFullscreen={isFullscreen}
          onFullscreenToggle={onFullscreenToggle}
        />,
      );
    }

    return buttons;
  };

  const standardButtons = renderStandardButtons();
  const showDivider = buttonConfig.showSearch && standardButtons.length > 1;

  return (
    <TableHeaderContainer>
      {shouldShowTitle && <TableHeaderTitle>{title}</TableHeaderTitle>}

      <TableHeaderActions>
        {renderCustomButtons()}

        {standardButtons.map((button, index) => (
          <React.Fragment key={button.key || index}>
            {button}
            {showDivider && index === 0 && standardButtons.length > 1 && (
              <Divider orientation="vertical" flexItem />
            )}
          </React.Fragment>
        ))}

        <IconPreloader />
      </TableHeaderActions>
    </TableHeaderContainer>
  );
};
